<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@kyoto2/components/ui/button'
import IconButton from '@/components/IconButton.vue'
import { Separator } from '@kyoto2/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@kyoto2/components/ui/tooltip'
import {
  LayoutDashboard,
  ListTodo,
  Play,
  Plug,
  Clock,
  Layers,
  FileStack,
  Server,
  Settings,
  LogOut,
  Search,
  Users,
  BookOpen,
  Brain,
} from 'lucide-vue-next'
import { Badge } from '@kyoto2/components/ui/badge'
import { useBrandingStore } from '@/stores/branding'
import { useAiChatStore } from '@/stores/ai-chat'

withDefaults(defineProps<{
  compact?: boolean
}>(), {
  compact: false,
})

const emit = defineEmits<{
  navigate: []
}>()

const route = useRoute()
const branding = useBrandingStore()
const router = useRouter()
const auth = useAuthStore()
const aiChat = useAiChatStore()

type NavItem = { name: string; path: string; icon: typeof LayoutDashboard; roles?: string[] }

const navGroups: NavItem[][] = [
  [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Jobs', path: '/jobs', icon: Layers },
    { name: 'Runs', path: '/runs', icon: Play },
  ],
  [
    { name: 'Templates', path: '/job-templates', icon: FileStack },
    { name: 'Tasks', path: '/tasks', icon: ListTodo },
    { name: 'Connectors', path: '/connectors', icon: Plug },
    { name: 'Triggers', path: '/triggers', icon: Clock },
  ],
  [
    { name: 'Data Explorer', path: '/data-explorer', icon: Search },
    { name: 'Executors', path: '/executors', icon: Server },
    { name: 'Users', path: '/users', icon: Users, roles: ['superadmin', 'admin'] },
    { name: 'AI References', path: '/ai-references', icon: BookOpen, roles: ['superadmin', 'admin'] },
    { name: 'Settings', path: '/settings', icon: Settings },
  ],
]

const filteredGroups = computed(() =>
  navGroups
    .map(group => group.filter(item => {
      if (!item.roles) return true
      return auth.user?.role && item.roles.includes(auth.user.role)
    }))
    .filter(group => group.length > 0),
)

const isActive = (path: string) => {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}

function goTo(path: string) {
  router.push(path)
  emit('navigate')
}

async function handleLogout() {
  await auth.logout()
  router.push('/login')
}

function openAiAssistant() {
  aiChat.toggleSidebar(true)
  emit('navigate')
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0 bg-background">
    <div class="px-5 py-5">
      <div class="flex items-center gap-2.5">
        <img v-if="branding.iconUrl" :src="branding.iconUrl" :alt="branding.appName" class="size-7 rounded-md object-contain" />
        <div v-else class="size-7 rounded-md bg-foreground flex items-center justify-center">
          <span class="text-background text-xs font-bold">{{ branding.initial }}</span>
        </div>
        <div class="leading-none">
          <div class="text-sm font-semibold tracking-tight">{{ branding.appName }}</div>
          <div class="text-[11px] text-muted-foreground mt-0.5">Orchestrator</div>
        </div>
      </div>
    </div>

    <Separator />

    <nav class="flex-1 px-3 py-3 overflow-y-auto">
      <TooltipProvider :delay-duration="500">
        <div v-for="(group, gi) in filteredGroups" :key="gi">
          <Separator v-if="gi > 0" class="my-2" />
          <div class="space-y-0.5">
            <Tooltip v-for="item in group" :key="item.path">
              <TooltipTrigger as-child>
                <Button
                  :variant="isActive(item.path) ? 'secondary' : 'ghost'"
                  class="w-full justify-start gap-2.5 h-11 lg:h-9 px-3 text-[13px]"
                  :class="isActive(item.path) ? 'font-medium' : 'font-normal text-muted-foreground'"
                  @click="goTo(item.path)"
                >
                  <component :is="item.icon" class="size-4 shrink-0" />
                  {{ item.name }}
                </Button>
              </TooltipTrigger>
              <TooltipContent v-if="!compact" side="right" :side-offset="8">{{ item.name }}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </TooltipProvider>
    </nav>

    <Separator />

    <div class="px-3 py-2">
      <Button
        variant="ghost"
        class="w-full justify-start gap-2.5 h-11 lg:h-9 px-3 text-[13px] font-normal text-muted-foreground"
        :class="aiChat.sidebarOpen ? 'bg-accent text-foreground font-medium' : ''"
        @click="openAiAssistant"
      >
        <Brain class="size-4 shrink-0" />
        AI Assistant
        <span v-if="!compact" class="ml-auto text-[10px] text-muted-foreground">⌘⇧A</span>
      </Button>
    </div>

    <Separator />

    <div class="px-4 py-3 flex items-center justify-between">
      <div class="flex items-center gap-2 min-w-0">
        <div class="size-6 rounded-full bg-muted flex items-center justify-center shrink-0">
          <span class="text-[10px] font-medium text-muted-foreground uppercase">{{ auth.user?.username?.[0] ?? '?' }}</span>
        </div>
        <span class="text-xs text-muted-foreground truncate">{{ auth.user?.username }}</span>
        <Badge v-if="auth.user?.role" variant="outline" class="text-[10px] px-1.5 py-0 h-4 shrink-0">{{ auth.user.role }}</Badge>
      </div>
      <IconButton aria-label="Log out" class="shrink-0" @click="handleLogout">
        <LogOut class="size-3.5" />
      </IconButton>
    </div>
  </div>
</template>
